/**
 * ImagemViewer — visualizador de foto de solicitação com edição.
 *
 * Substitui os DOIS overlays ad-hoc que existiam no módulo (um na Gestão,
 * outro na tela do colaborador), que só ampliavam a imagem.
 *
 * Faz: zoom (botões, roda, pinça) com arrasto, girar 90° para os dois lados,
 * cortar, imprimir (com cabeçalho), compartilhar, baixar e restaurar original.
 *
 * Girar e cortar SALVAM sozinhos: chamam `onSalvar(dataUrl)` e o backend
 * guarda o estado de origem na primeira edição, o que faz "Restaurar original"
 * devolver a foto que o colaborador enviou — importante porque foto de
 * assistência é prova de defeito.
 *
 * Sem `onSalvar` o visualizador fica só-leitura (some girar/cortar/restaurar).
 *
 * Props:
 * - `foto`: { id, imagem (dataURL), legenda, editada }
 * - `cabecalho`: { titulo, detalhe, lote } — usado na impressão
 * - `onFechar()`
 * - `onSalvar(dataUrl)`: Promise — persiste giro/corte (opcional)
 * - `onRestaurar()`: Promise<dataUrl> — devolve o original (opcional)
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FiCheck, FiCrop, FiDownload, FiPrinter, FiRotateCcw, FiRotateCw,
  FiShare2, FiX, FiZoomIn, FiZoomOut,
} from "react-icons/fi";

import { useToast } from "../../context/ToastContext";
import { imprimirImagens } from "../../pages/Suprimentos/Solicitacoes/imprimirImagens";
import {
  caixaDaFoto, cortarDataUrl, dataUrlParaBlob, girarDataUrl,
} from "../../utils/imagemCanvas";
import {
  AcaoBtn, Arte, Aviso, Barra, CorteAlca, CorteCamada, CorteJanela, Overlay,
  Palco, Topo, TopoTexto,
} from "./styles";

const ZOOM_MIN = 1;
const ZOOM_MAX = 5;
const ZOOM_PASSO = 0.5;
const CORTE_INICIAL = { x: 0.1, y: 0.1, largura: 0.8, altura: 0.8 };
const CORTE_MINIMO = 0.08;

const limitar = (v, min, max) => Math.min(max, Math.max(min, v));

export default function ImagemViewer({
  foto, cabecalho, onFechar, onSalvar, onRestaurar,
}) {
  const toast = useToast();
  const palcoRef = useRef(null);
  const arteRef = useRef(null);

  const [imagem, setImagem] = useState(foto?.imagem || "");
  const [editada, setEditada] = useState(Boolean(foto?.editada));
  const [vista, setVista] = useState({ escala: 1, tx: 0, ty: 0 });
  const [salvando, setSalvando] = useState(false);
  const [corte, setCorte] = useState(null);
  // A caixa REAL da imagem dentro do palco. Como a imagem usa object-fit
  // scale-down, ela quase nunca preenche o palco — sobra tarja. Medir esta caixa
  // é o que faz a janela de corte corresponder ao que o usuário vê: usar o
  // palco recortava outra região da foto.
  const [caixa, setCaixa] = useState(null);

  // Trava SÍNCRONA: `salvando` é estado e só chega no próximo render, então
  // dois toques rápidos em "girar" passavam os dois e um giro se perdia.
  const ocupadoRef = useRef(false);
  const montadoRef = useRef(true);
  const arrasteRef = useRef(null);
  const ponteirosRef = useRef(new Map()); // pinça: id -> {x, y}
  const focoAnteriorRef = useRef(null);

  const podeEditar = typeof onSalvar === "function";
  const emCorte = Boolean(corte);

  useEffect(() => {
    montadoRef.current = true;
    return () => { montadoRef.current = false; };
  }, []);

  // Reinicia o estado visual quando muda A FOTO — de propósito só pelo `id`.
  // Incluir `imagem` nas dependências faria este efeito rodar a cada
  // salvamento (o pai atualiza a prop com a imagem nova) e disputar com o
  // estado local, podendo desfazer visualmente o giro que acabou de gravar.
  useEffect(() => {
    setImagem(foto?.imagem || "");
    setEditada(Boolean(foto?.editada));
    setVista({ escala: 1, tx: 0, ty: 0 });
    setCorte(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foto?.id]);

  // Mantém o marcador em dia quando o pai recarrega o detalhe.
  useEffect(() => { setEditada(Boolean(foto?.editada)); }, [foto?.editada]);

  /**
   * Calcula a caixa REAL da foto dentro do palco.
   *
   * A `<img>` preenche o palco inteiro (ver `Arte` em styles.jsx), então o
   * `getBoundingClientRect` dela devolve o palco, incluindo a tarja preta do
   * letterbox. Quem quer a foto tem que refazer a conta do `object-fit:
   * scale-down` — é essa caixa que a janela de corte usa; medir errado aqui
   * recortaria outra região da imagem, e o corte salva sozinho.
   */
  const medirArte = useCallback(() => {
    const img = arteRef.current;
    const palco = palcoRef.current;
    if (!img || !palco) return;
    const { naturalWidth: nw, naturalHeight: nh } = img;
    if (!nw || !nh) return; // ainda carregando: dividir aqui daria NaN
    const ri = img.getBoundingClientRect();
    const rp = palco.getBoundingClientRect();
    if (!ri.width || !ri.height) return;

    const dentro = caixaDaFoto(ri, { width: nw, height: nh });
    setCaixa({
      left: ri.left - rp.left + dentro.left,
      top: ri.top - rp.top + dentro.top,
      width: dentro.width,
      height: dentro.height,
    });
  }, []);

  // `emCorte` nas dependências NÃO é decorativo: `getBoundingClientRect`
  // devolve a caixa JÁ TRANSFORMADA, e entrar no corte zera o transform
  // (`estiloArte`). Sem remedir aqui, a janela de corte usaria a caixa do
  // estado ampliado, ficaria fora do lugar e recortaria outra região da foto
  // — que ainda por cima salva sozinha.
  useLayoutEffect(() => {
    medirArte();
    window.addEventListener("resize", medirArte);
    window.addEventListener("orientationchange", medirArte);
    return () => {
      window.removeEventListener("resize", medirArte);
      window.removeEventListener("orientationchange", medirArte);
    };
  }, [medirArte, imagem, emCorte]);

  // ---------- foco e teclado ----------

  useEffect(() => {
    focoAnteriorRef.current = document.activeElement;
    palcoRef.current?.closest("[data-viewer]")?.focus?.();
    return () => { focoAnteriorRef.current?.focus?.(); };
  }, []);

  useEffect(() => {
    window.history.pushState({ imagemViewer: true }, "");
    const aoVoltar = (e) => {
      if (e.state?.imagemViewer) return; // ainda numa entrada nossa
      // Salvando: repõe a entrada e ignora. Fechar no meio do POST deixava a
      // pessoa sem saber se a edição pegou.
      if (ocupadoRef.current) {
        window.history.pushState({ imagemViewer: true }, "");
        return;
      }
      // No corte, o voltar do Android cancela o corte — não sai da foto.
      if (corteRef.current) {
        window.history.pushState({ imagemViewer: true }, "");
        setCorte(null);
        return;
      }
      onFechar?.();
    };
    // CAPTURA + stopImmediatePropagation: o SlideOverPanel por baixo também
    // escuta Esc no window; sem consumir aqui, um Esc fechava a foto E o
    // painel da solicitação junto, jogando o gestor de volta na lista.
    const aoTeclar = (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();
      if (ocupadoRef.current) return; // salvando: não fecha nem cancela
      if (corteRef.current) setCorte(null); // Esc no corte só cancela o corte
      else fecharPeloHistorico();
    };
    window.addEventListener("popstate", aoVoltar);
    window.addEventListener("keydown", aoTeclar, true);
    return () => {
      window.removeEventListener("popstate", aoVoltar);
      window.removeEventListener("keydown", aoTeclar, true);
      if (window.history.state?.imagemViewer) window.history.back();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // O handler de Esc é registrado uma vez só; lê o modo corte por ref.
  const corteRef = useRef(null);
  useEffect(() => { corteRef.current = corte; }, [corte]);

  const fecharPeloHistorico = () => {
    if (window.history.state?.imagemViewer) window.history.back();
    else onFechar?.();
  };

  // ---------- persistência ----------

  /** Devolve true quando persistiu. Em falha, a tela mantém a imagem antiga —
   *  mostrar uma edição não salva é pior que não editar. */
  const salvar = useCallback(async (novaDataUrl, rotulo) => {
    try {
      await onSalvar(novaDataUrl);
      if (!montadoRef.current) return true;
      setImagem(novaDataUrl);
      setEditada(true);
      setVista({ escala: 1, tx: 0, ty: 0 });
      toast.success(`${rotulo} salvo.`);
      return true;
    } catch (e) {
      // O toast dispara MESMO desmontado. O provider é ancestral e continua
      // vivo, e o service usa `silentError` — este é o único lugar onde o erro
      // aparece. Silenciar por estar desmontado mandava o operador embora
      // achando que girou a foto quando o POST tinha falhado.
      toast.error(e?.message || `Não foi possível salvar o ${rotulo.toLowerCase()}.`);
      return false;
    }
  }, [onSalvar, toast]);

  const comTrava = async (fn) => {
    if (ocupadoRef.current) return;
    ocupadoRef.current = true;
    setSalvando(true);
    try {
      await fn();
    } finally {
      ocupadoRef.current = false;
      if (montadoRef.current) setSalvando(false);
    }
  };

  const girar = (graus) => comTrava(async () => {
    try {
      await salvar(await girarDataUrl(imagem, graus), "Giro");
    } catch (e) {
      toast.error(e?.message || "Não foi possível girar a imagem.");
    }
  });

  /**
   * Entra no corte SEMPRE com a foto inteira à vista.
   *
   * O corte já renderiza sem transform; zerar a vista aqui mantém o estado
   * coerente com o que está na tela — senão, ao cancelar o corte, a foto
   * voltaria de repente para um zoom que o usuário nem lembra ter deixado.
   */
  const abrirCorte = () => {
    setVista({ escala: 1, tx: 0, ty: 0 });
    setCorte(CORTE_INICIAL);
  };

  const confirmarCorte = () => comTrava(async () => {
    try {
      const nova = await cortarDataUrl(imagem, corte);
      // Só descarta a seleção DEPOIS de persistir: falha de rede não pode
      // apagar o enquadramento que a pessoa acabou de fazer.
      if (await salvar(nova, "Corte")) setCorte(null);
    } catch (e) {
      toast.error(e?.message || "Não foi possível cortar a imagem.");
    }
  });

  const restaurar = () => comTrava(async () => {
    // Descarta o giro/corte no servidor e não tem volta — e o botão fica ao
    // lado de "Baixar" numa barra que reflui no celular. Confirma antes.
    const ok = window.confirm(
      "Restaurar a foto original?\n\n" +
      "Os giros e cortes feitos nesta foto serão descartados. " +
      "Não é possível desfazer.",
    );
    if (!ok) return;
    try {
      const original = await onRestaurar();
      if (!montadoRef.current) return;
      if (original) setImagem(original);
      setEditada(false);
      setVista({ escala: 1, tx: 0, ty: 0 });
      toast.success("Foto original restaurada.");
    } catch (e) {
      toast.error(e?.message || "Não foi possível restaurar a foto.");
    }
  });

  // ---------- saídas ----------

  const nomeArquivo = () => {
    const base = [cabecalho?.titulo, cabecalho?.detalhe].filter(Boolean).join("-")
      .replace(/[^\w\-.]+/g, "-").slice(0, 60) || "foto";
    return `${base}.jpg`;
  };

  const imprimir = () => {
    if (!imprimirImagens([{ imagem, cabecalho }])) {
      toast.error("O navegador bloqueou a janela de impressão.");
    }
  };

  const baixar = () => {
    const url = URL.createObjectURL(dataUrlParaBlob(imagem));
    const a = document.createElement("a");
    a.href = url;
    a.download = nomeArquivo();
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const compartilhar = async () => {
    const arquivo = new File([dataUrlParaBlob(imagem)], nomeArquivo(), {
      type: "image/jpeg",
    });
    const dados = {
      files: [arquivo],
      title: cabecalho?.titulo || "Foto da solicitação",
      text: [cabecalho?.titulo, cabecalho?.detalhe].filter(Boolean).join(" — "),
    };
    if (navigator.canShare?.(dados)) {
      try {
        await navigator.share(dados);
      } catch (e) {
        if (e?.name !== "AbortError") toast.error("Não foi possível compartilhar.");
      }
      return;
    }
    baixar();
    toast.info("Este navegador não compartilha arquivos — a imagem foi baixada.");
  };

  // ---------- zoom, pan e pinça ----------

  const aplicarZoom = (delta, foco) => setVista((v) => {
    const escala = limitar(Number((v.escala + delta).toFixed(2)), ZOOM_MIN, ZOOM_MAX);
    if (escala === ZOOM_MIN) return { escala, tx: 0, ty: 0 };
    // Sem foco (botões), amplia pelo centro. Com foco (roda/pinça), mantém o
    // ponto sob o dedo no lugar.
    if (!foco || !caixa) return travarDentroDoPalco({ ...v, escala });
    const razao = escala / v.escala;
    // Trava também aqui: diminuir o zoom com o foco numa borda deixaria a
    // foto deslocada além do que a nova escala permite.
    return travarDentroDoPalco({
      escala,
      tx: foco.x - razao * (foco.x - v.tx),
      ty: foco.y - razao * (foco.y - v.ty),
    });
  });

  /**
   * Impede que o arrasto jogue a foto para fora da tela.
   *
   * O deslocamento máximo é a metade do que a imagem ampliada "sobra" para
   * fora do palco: nesse limite a borda da foto encosta na borda do palco e
   * para. Sem isso dava para empurrar a imagem até sumir — e no celular, sem
   * barra de rolagem, não havia como trazê-la de volta a não ser fechando.
   */
  const travarDentroDoPalco = ({ escala, tx, ty }) => {
    if (!caixa) return { escala, tx, ty };
    const folgaX = Math.max(0, (caixa.width * escala - caixa.width) / 2);
    const folgaY = Math.max(0, (caixa.height * escala - caixa.height) / 2);
    return {
      escala,
      tx: limitar(tx, -folgaX, folgaX),
      ty: limitar(ty, -folgaY, folgaY),
    };
  };

  const pontoNoPalco = (e) => {
    const rp = palcoRef.current?.getBoundingClientRect();
    if (!rp) return { x: 0, y: 0 };
    return { x: e.clientX - rp.left - rp.width / 2, y: e.clientY - rp.top - rp.height / 2 };
  };

  const aoRolar = (e) => {
    if (emCorte) return;
    aplicarZoom(e.deltaY < 0 ? ZOOM_PASSO : -ZOOM_PASSO, pontoNoPalco(e));
  };

  const aoDescerNoPalco = (e) => {
    if (emCorte) return;
    ponteirosRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (ponteirosRef.current.size === 1 && vista.escala > ZOOM_MIN) {
      arrasteRef.current = { modo: "pan", x: e.clientX, y: e.clientY, base: vista };
    }
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const aoMoverNoPalco = (e) => {
    if (emCorte) return;
    const ponteiros = ponteirosRef.current;
    if (!ponteiros.has(e.pointerId)) return;
    ponteiros.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (ponteiros.size >= 2) { // pinça
      const [a, b] = [...ponteiros.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const anterior = arrasteRef.current?.dist;
      arrasteRef.current = { modo: "pinca", dist };
      if (anterior) {
        const rp = palcoRef.current?.getBoundingClientRect();
        const foco = rp
          ? { x: (a.x + b.x) / 2 - rp.left - rp.width / 2,
              y: (a.y + b.y) / 2 - rp.top - rp.height / 2 }
          : null;
        aplicarZoom(((dist - anterior) / 160) * ZOOM_MAX, foco);
      }
      return;
    }
    const a = arrasteRef.current;
    if (a?.modo === "pan") {
      setVista(travarDentroDoPalco({
        escala: a.base.escala,
        tx: a.base.tx + (e.clientX - a.x),
        ty: a.base.ty + (e.clientY - a.y),
      }));
    }
  };

  const aoSoltarNoPalco = (e) => {
    ponteirosRef.current.delete(e.pointerId);
    if (ponteirosRef.current.size < 2) arrasteRef.current = null;
  };

  // ---------- corte ----------

  /** Fração 0..1 DENTRO da imagem renderizada (não do palco). */
  const fracaoDoPonteiro = (e) => {
    const rp = palcoRef.current?.getBoundingClientRect();
    if (!rp || !caixa) return { fx: 0, fy: 0 };
    return {
      fx: limitar((e.clientX - rp.left - caixa.left) / caixa.width, 0, 1),
      fy: limitar((e.clientY - rp.top - caixa.top) / caixa.height, 0, 1),
    };
  };

  const iniciarArrasteCorte = (modo, canto) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    arrasteRef.current = { modo, canto, inicio: fracaoDoPonteiro(e), base: corte };
  };

  const moverArrasteCorte = (e) => {
    const a = arrasteRef.current;
    if (!a || !corte || (a.modo !== "mover" && a.modo !== "canto")) return;
    const { fx, fy } = fracaoDoPonteiro(e);
    const dx = fx - a.inicio.fx;
    const dy = fy - a.inicio.fy;
    const b = a.base;

    if (a.modo === "mover") {
      setCorte({
        ...b,
        x: limitar(b.x + dx, 0, 1 - b.largura),
        y: limitar(b.y + dy, 0, 1 - b.altura),
      });
      return;
    }
    const oeste = a.canto.includes("w");
    const norte = a.canto.includes("n");
    let { x, y, largura, altura } = b;
    if (oeste) {
      const nx = limitar(b.x + dx, 0, b.x + b.largura - CORTE_MINIMO);
      largura = b.largura + (b.x - nx);
      x = nx;
    } else {
      largura = limitar(b.largura + dx, CORTE_MINIMO, 1 - b.x);
    }
    if (norte) {
      const ny = limitar(b.y + dy, 0, b.y + b.altura - CORTE_MINIMO);
      altura = b.altura + (b.y - ny);
      y = ny;
    } else {
      altura = limitar(b.altura + dy, CORTE_MINIMO, 1 - b.y);
    }
    setCorte({ x, y, largura, altura });
  };

  if (!foto) return null;

  const estiloArte = emCorte
    ? { transform: "none" }
    : { transform: `translate(${vista.tx}px, ${vista.ty}px) scale(${vista.escala})` };

  return createPortal(
    <Overlay data-viewer tabIndex={-1} role="dialog" aria-modal="true"
      aria-label="Visualizar imagem">
      <Topo>
        <TopoTexto>
          <strong>{cabecalho?.titulo || "Foto"}</strong>
          {cabecalho?.detalhe && <span>{cabecalho.detalhe}</span>}
        </TopoTexto>
        {cabecalho?.lote && <AcaoBtn as="span">{cabecalho.lote}</AcaoBtn>}
        {/* Travado enquanto grava: sair no meio do POST deixava a pessoa sem
            saber se a edição pegou. */}
        <AcaoBtn type="button" onClick={fecharPeloHistorico} disabled={salvando}
          aria-label="Fechar" title={salvando ? "Aguarde o salvamento" : "Fechar"}>
          <FiX size={20} />
        </AcaoBtn>
      </Topo>

      {salvando && <Aviso>Salvando… não feche esta tela.</Aviso>}

      {editada && !emCorte && !salvando && (
        <Aviso>Esta foto foi editada. O original está guardado.</Aviso>
      )}

      <Palco
        ref={palcoRef}
        onWheel={aoRolar}
        onPointerDown={aoDescerNoPalco}
        onPointerMove={aoMoverNoPalco}
        onPointerUp={aoSoltarNoPalco}
        onPointerCancel={aoSoltarNoPalco}
        $arrastavel={!emCorte && vista.escala > ZOOM_MIN}
      >
        <Arte
          ref={arteRef}
          src={imagem}
          alt={foto.legenda || cabecalho?.detalhe || "Foto da solicitação"}
          style={estiloArte}
          onLoad={medirArte}
          draggable={false}
        />
        {emCorte && caixa && (
          <CorteCamada
            style={{
              left: caixa.left, top: caixa.top,
              width: caixa.width, height: caixa.height,
            }}
            onPointerMove={moverArrasteCorte}
            onPointerUp={() => { arrasteRef.current = null; }}
            onPointerCancel={() => { arrasteRef.current = null; }}
          >
            <CorteJanela
              style={{
                left: `${corte.x * 100}%`, top: `${corte.y * 100}%`,
                width: `${corte.largura * 100}%`, height: `${corte.altura * 100}%`,
              }}
              onPointerDown={iniciarArrasteCorte("mover")}
            >
              {["nw", "ne", "sw", "se"].map((canto) => (
                <CorteAlca key={canto} data-canto={canto}
                  onPointerDown={iniciarArrasteCorte("canto", canto)} />
              ))}
            </CorteJanela>
          </CorteCamada>
        )}
      </Palco>

      <Barra>
        {emCorte ? (
          <>
            <AcaoBtn type="button" onClick={() => setCorte(null)} disabled={salvando}>
              Cancelar
            </AcaoBtn>
            <AcaoBtn type="button" $destaque onClick={confirmarCorte} disabled={salvando}>
              <FiCheck size={18} /> {salvando ? "Salvando…" : "Aplicar corte"}
            </AcaoBtn>
          </>
        ) : (
          <>
            <AcaoBtn type="button" onClick={() => aplicarZoom(-ZOOM_PASSO)}
              disabled={vista.escala <= ZOOM_MIN} aria-label="Diminuir zoom">
              <FiZoomOut size={18} />
            </AcaoBtn>
            <AcaoBtn type="button" onClick={() => aplicarZoom(ZOOM_PASSO)}
              disabled={vista.escala >= ZOOM_MAX} aria-label="Aumentar zoom">
              <FiZoomIn size={18} />
            </AcaoBtn>

            {podeEditar && (
              <>
                <AcaoBtn type="button" onClick={() => girar(-90)} disabled={salvando}
                  aria-label="Girar para a esquerda">
                  <FiRotateCcw size={18} />
                </AcaoBtn>
                <AcaoBtn type="button" onClick={() => girar(90)} disabled={salvando}
                  aria-label="Girar para a direita">
                  <FiRotateCw size={18} />
                </AcaoBtn>
                <AcaoBtn type="button" onClick={abrirCorte}
                  disabled={salvando || !caixa} aria-label="Cortar">
                  <FiCrop size={18} />
                </AcaoBtn>
              </>
            )}

            <AcaoBtn type="button" onClick={imprimir} aria-label="Imprimir">
              <FiPrinter size={18} />
            </AcaoBtn>
            <AcaoBtn type="button" onClick={compartilhar} aria-label="Compartilhar">
              <FiShare2 size={18} />
            </AcaoBtn>
            <AcaoBtn type="button" onClick={baixar} aria-label="Baixar">
              <FiDownload size={18} />
            </AcaoBtn>

            {podeEditar && editada && typeof onRestaurar === "function" && (
              <AcaoBtn type="button" onClick={restaurar} disabled={salvando}>
                Restaurar original
              </AcaoBtn>
            )}
          </>
        )}
      </Barra>
    </Overlay>,
    document.body,
  );
}
